import React, { useMemo } from 'react';
import { DataGridPro, getGridSingleSelectOperators, GridColDef, GridFilterModel, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-pro';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faStar } from '@fortawesome/pro-light-svg-icons';
import { ChatModel, ChatSort } from '../../../../api-service/clients';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { formatDateTime } from '../../../../utilities/date-helper';
import { ForumGridOverlay } from '../forum-grid-overlay';
import { citizenText, hasUnreadLatestMessage, isNewChat, latestMessageText, participantsText } from './chat-list-row';

export const forumChatsColumns: GridColDef[] = [
  {
    field: 'citizen',
    headerName: 'Borger',
    valueGetter: params => citizenText(params.row as ChatModel),
    filterable: false,
    width: 200,
  },
  {
    field: 'title',
    headerName: 'Titel',
    sortable: false,
    filterable: false,
    width: 200,
  },
  {
    field: 'chatMessages',
    headerName: 'Seneste besked',
    valueGetter: params => latestMessageText(params.row as ChatModel),
    sortable: false,
    filterable: false,
    flex: 1,
  },
  {
    field: 'participants',
    headerName: 'Deltagere',
    valueGetter: params => participantsText(params.row as ChatModel),
    sortable: false,
    filterable: false,
    width: 250,
  },
  {
    field: 'lastActivityDate',
    headerName: 'Seneste aktivitet',
    valueFormatter: ({ value }) => formatDateTime(value),
    filterable: false,
    width: 150,
  },
  {
    field: 'creationDate',
    headerName: 'Oprettet',
    valueFormatter: ({ value }) => formatDateTime(value),
    filterable: false,
    width: 150,
  },
];

interface IChatTitleWithMarkersProps {
  chat: ChatModel;
  listedAt: Date;
}

function ChatTitleWithMarkers(props: IChatTitleWithMarkersProps) {
  const { chat, listedAt } = props;

  return (
    <Box display="flex" alignItems="center" gap={1} overflow="hidden">
      {isNewChat(chat, listedAt) && (
        <Tooltip title="Ny chat">
          <FontAwesomeIcon icon={faStar} />
        </Tooltip>
      )}
      {chat.isConfidential && (
        <Tooltip title="Fortrolig">
          <FontAwesomeIcon icon={faLock} />
        </Tooltip>
      )}
      <Box component="span" overflow="hidden" textOverflow="ellipsis">
        {chat.title}
      </Box>
    </Box>
  );
}

export type ChatStatus = 'open' | 'closed';

const filterValues: { value: ChatStatus; text: string }[] = [
  { value: 'open', text: 'Vis åbne' },
  { value: 'closed', text: 'Vis afsluttede' },
];

export const unseenActivityRowClass = 'has-unseen-activity';

const sortByField: Record<string, ChatSort> = {
  citizen: ChatSort.CitizenName,
  lastActivityDate: ChatSort.LatestActivityDate,
  creationDate: ChatSort.CreationDate,
};

const fieldByChatSort = new Map(Object.entries(sortByField).map(([field, chatSort]) => [chatSort, field]));

const caseworkerField = 'caseworker';
const outlinedFilterInput = { variant: 'outlined' } as const;

export interface ICaseworkerOption {
  id: string;
  name: string;
}

export interface IForumChatsTabComponentProps {
  chats: ChatModel[];
  listedAt: Date;
  caseworkers: ICaseworkerOption[];
  isLoading: boolean;
  hasFailed: boolean;
  status: ChatStatus;
  onStatusChange: (status: ChatStatus) => void;
  chatSort: ChatSort;
  onChatSortChange: (chatSort: ChatSort) => void;
  caseworkerId: string;
  onChatClick: (chat: ChatModel) => void;
  onCaseworkerChange: (caseworkerId: string) => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (unreadOnly: boolean) => void;
  showCaseworkerFilter?: boolean;
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRefresh: () => void;
  onCreateChat?: () => void;
}

function NoChats() {
  return <ForumGridOverlay message="Der er ingen beskeder." />;
}

function FailedToLoadChats() {
  return <ForumGridOverlay message="Beskederne kunne ikke hentes." />;
}

interface IForumChatsToolbarProps {
  unreadOnly: boolean;
  onUnreadOnlyChange: (unreadOnly: boolean) => void;
  status: ChatStatus;
  onStatusChange: (status: ChatStatus) => void;
  onRefresh: () => void;
  isLoading: boolean;
  showFilterButton: boolean;
  onCreateChat?: () => void;
}

// Declared out here so the grid keeps one toolbar instance: rebuilding it per render closes the open dropdown.
function ForumChatsToolbar(props: IForumChatsToolbarProps) {
  const { unreadOnly, onUnreadOnlyChange, status, onStatusChange, onRefresh, isLoading, showFilterButton, onCreateChat } = props;

  const unreadOnlyCheckbox = (
    <FormControlLabel
      label="Vis kun uløste beskeder"
      control={<Checkbox size="small" sx={{ py: 0 }} checked={unreadOnly} onChange={event => onUnreadOnlyChange(event.target.checked)} />}
    />
  );

  return FasitDataGridToolbar({
    title: 'Chats',
    filterValues,
    activeFilter: status,
    onFilterChange: event => onStatusChange(event.target.value as ChatStatus),
    onAddButtonClick: onCreateChat,
    addButtonTooltip: 'Opret besked',
    customInputBeforeFilter: unreadOnlyCheckbox,
    showFilterButton,
    onRefreshClick: onRefresh,
    refreshDisabled: isLoading,
  });
}

export function ForumChatsTabComponent(props: IForumChatsTabComponentProps) {
  const {
    chats,
    listedAt,
    caseworkers,
    isLoading,
    hasFailed,
    status,
    onStatusChange,
    chatSort,
    onChatSortChange,
    caseworkerId,
    onChatClick,
    onCaseworkerChange,
    unreadOnly,
    onUnreadOnlyChange,
    showCaseworkerFilter,
    page,
    pageSize,
    rowCount,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    onCreateChat,
  } = props;

  function handleSortModelChange(model: GridSortModel) {
    const chosenSort = model[0] && sortByField[model[0].field];
    if (chosenSort !== undefined) {
      onChatSortChange(chosenSort);
    }
  }

  function handleFilterModelChange(model: GridFilterModel) {
    const caseworkerItem = model.items.find(item => item.columnField === caseworkerField);
    onCaseworkerChange(caseworkerItem?.value ?? '');
  }

  function handleRowClick(params: { row: ChatModel }) {
    onChatClick(params.row);
  }

  const columns = useMemo<GridColDef[]>(() => {
    const columnsWithTitleRenderer = forumChatsColumns.map(column => {
      if (column.field === 'title') {
        return { ...column, renderCell: ({ row }: GridRenderCellParams<ChatModel>) => <ChatTitleWithMarkers chat={row} listedAt={listedAt} /> };
      }
      return column;
    });
    if (!showCaseworkerFilter) {
      return columnsWithTitleRenderer;
    }
    const caseworkerColumn: GridColDef = {
      field: caseworkerField,
      headerName: 'Sagsbehandler',
      type: 'singleSelect',
      valueOptions: caseworkers.map(caseworker => ({ value: caseworker.id, label: caseworker.name })),
      valueGetter: params => (params.row as ChatModel).caseworker?.id,
      filterOperators: getGridSingleSelectOperators().filter(operator => operator.value === 'is'),
      sortable: false,
      width: 160,
    };
    return [...columnsWithTitleRenderer, caseworkerColumn];
  }, [showCaseworkerFilter, caseworkers, listedAt]);

  const sortModel = useMemo<GridSortModel>(() => [{ field: fieldByChatSort.get(chatSort) ?? 'lastActivityDate', sort: 'asc' }], [chatSort]);

  const filterModel = useMemo<GridFilterModel>(
    () => ({ items: caseworkerId ? [{ id: caseworkerField, columnField: caseworkerField, operatorValue: 'is', value: caseworkerId }] : [] }),
    [caseworkerId]
  );

  const components = useMemo(() => ({ Toolbar: ForumChatsToolbar, NoRowsOverlay: hasFailed ? FailedToLoadChats : NoChats }), [hasFailed]);

  return (
    <DataGridPro<ChatModel>
      rows={chats}
      columns={columns}
      columnBuffer={columns.length}
      loading={isLoading}
      autoHeight
      density="compact"
      getRowClassName={({ row }) => (hasUnreadLatestMessage(row) ? unseenActivityRowClass : '')}
      sx={{ [`& .${unseenActivityRowClass}`]: { fontWeight: 'bold' } }}
      onRowClick={handleRowClick}
      components={components}
      componentsProps={{
        toolbar: { unreadOnly, onUnreadOnlyChange, status, onStatusChange, onRefresh, isLoading, showFilterButton: !!showCaseworkerFilter, onCreateChat },
        panel: { disablePortal: true, placement: 'bottom-end' },
        filterPanel: {
          filterFormProps: { columnInputProps: outlinedFilterInput, operatorInputProps: outlinedFilterInput, valueInputProps: { InputComponentProps: outlinedFilterInput } },
        },
      }}
      // The grid only holds one page, so sorting and filtering must be done by the service.
      sortingMode="server"
      sortingOrder={['asc']}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      filterMode="server"
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      disableColumnMenu
      pagination
      paginationMode="server"
      page={page}
      pageSize={pageSize}
      rowsPerPageOptions={[10, 25, 50, 100]}
      rowCount={rowCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
